import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export interface LocacoesAnalyticsChartsData {
  mensal: Array<{ name: string; novas: number; canceladas: number }>;
  diario: Array<{ name: string; novas: number; canceladas: number }>;
  topModeloMensal: Array<{
    name: string;
    marca: string;
    modelo: string;
    quantidade: number;
  }>;
  topPlacas: Array<{ placa: string; marca: string; modelo: string; quantidade: number }>;
}

const COLOR_NOVAS = 'hsl(222 47% 18%)';
const COLOR_CANCEL = '#ca8a04';

export const DiretoriaLocacoesCharts: React.FC<{ data: LocacoesAnalyticsChartsData | undefined }> = ({
  data,
}) => {
  const topModeloBars = useMemo(
    () =>
      (data?.topModeloMensal ?? []).map((row) => ({
        name: row.name,
        label: `${row.marca} ${row.modelo}`.trim(),
        quantidade: row.quantidade,
      })),
    [data?.topModeloMensal]
  );

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Locações mês a mês</CardTitle>
          <p className="text-xs text-muted-foreground">Criadas no período (empresa selecionada)</p>
        </CardHeader>
        <CardContent className="h-64 pt-0">
          {(data?.mensal?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data!.mensal}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="novas" name="Criadas" stroke={COLOR_NOVAS} strokeWidth={2} dot={false} />
                <Line
                  type="monotone"
                  dataKey="canceladas"
                  name="Canceladas"
                  stroke={COLOR_CANCEL}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Sem dados no período
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Locações dia a dia</CardTitle>
          <p className="text-xs text-muted-foreground">Criadas e cancelamentos no intervalo</p>
        </CardHeader>
        <CardContent className="h-64 pt-0">
          {(data?.diario?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data!.diario}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="novas" name="Criadas" stroke={COLOR_NOVAS} strokeWidth={2} dot={false} />
                <Line
                  type="monotone"
                  dataKey="canceladas"
                  name="Canceladas"
                  stroke={COLOR_CANCEL}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Sem dados no período
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Modelo que mais alugou (mês a mês)</CardTitle>
        </CardHeader>
        <CardContent className="h-64 pt-0">
          {topModeloBars.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topModeloBars} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={56} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v: number, _n, item) => [
                    `${v} · ${(item?.payload as { label?: string })?.label ?? ''}`,
                    'Qtd',
                  ]}
                />
                <Bar dataKey="quantidade" name="Locações" fill={COLOR_NOVAS} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Sem dados no período
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Placas com mais locações</CardTitle>
          <p className="text-xs text-muted-foreground">Locações distintas no período</p>
        </CardHeader>
        <CardContent className="pt-0">
          {(data?.topPlacas?.length ?? 0) > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-2 font-medium">#</th>
                    <th className="py-2 pr-2 font-medium">Placa</th>
                    <th className="py-2 pr-2 font-medium">Modelo</th>
                    <th className="py-2 text-right font-medium">Locações</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.topPlacas.map((row, i) => (
                    <tr key={`${row.placa}-${i}`} className="border-b border-border/60">
                      <td className="py-2 pr-2 tabular-nums text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-2 font-medium">{row.placa}</td>
                      <td className="py-2 pr-2 text-muted-foreground">
                        {[row.marca, row.modelo].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="py-2 text-right tabular-nums font-semibold">{row.quantidade}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">Sem dados no período</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
